import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, getCurrencySymbol } from '@/lib/utils';
import { db } from '@/lib/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { CalendarIcon, Plus, X, Printer, Download, AlertCircle, ArrowRight, Trash2 } from 'lucide-react';
import type { LineItem, Tax, Discount } from '@/types/invoice';
import { TemplatePicker } from '@/components/invoice/TemplatePicker';
import { InvoicePrintView } from '@/components/invoice/InvoicePrintView';
import { QuotationPrintView } from '@/components/invoice/QuotationPrintView';
import { exportInvoiceToPDF, exportQuotationToPDF } from '@/lib/export';
import { useToast } from '@/components/ui/use-toast';
import { INVOICE_TEMPLATES, type TemplateId } from '@/components/invoice/InvoiceTemplates';
import { isValidEmail, isValidPhone } from '@/lib/validation';

const COMMON_TAXES = [
  { label: 'Sales Tax 6%', type: 'percentage' as const, value: 6, enabled: true },
  { label: 'Sales Tax 8.25%', type: 'percentage' as const, value: 8.25, enabled: true },
  { label: 'Sales Tax 7%', type: 'percentage' as const, value: 7, enabled: true },
  { label: 'VAT 20%', type: 'percentage' as const, value: 20, enabled: true },
  { label: 'VAT 15%', type: 'percentage' as const, value: 15, enabled: true },
];

// --- Payment Terms ---
const PAYMENT_TERMS = [
  { value: 'net7',       label: 'Net 7',       days: 7,    description: 'Due in 7 days',                  bestFor: 'Freelancers, small projects, repeat clients' },
  { value: 'net10',      label: 'Net 10',      days: 10,   description: 'Due in 10 days',                 bestFor: 'Service businesses, professional services' },
  { value: 'net15',      label: 'Net 15',      days: 15,   description: 'Due in 15 days',                 bestFor: 'Healthcare, IT services, consulting' },
  { value: 'net30',      label: 'Net 30',      days: 30,   description: 'Due in 30 days',                 bestFor: 'Standard B2B default across most industries' },
  { value: 'net45',      label: 'Net 45',      days: 45,   description: 'Due in 45 days',                 bestFor: 'Mid-size corporate clients' },
  { value: 'net60',      label: 'Net 60',      days: 60,   description: 'Due in 60 days',                 bestFor: 'Large corporations, manufacturing' },
  { value: 'net90',      label: 'Net 90',      days: 90,   description: 'Due in 90 days',                 bestFor: 'Enterprise, government contracts' },
  { value: 'netMonthly', label: 'Net Monthly', days: null, description: 'Due by end of the current month', bestFor: 'Recurring service contracts' },
  { value: 'custom',     label: 'Custom',      days: null, description: 'Pick a specific date',            bestFor: '' },
] as const;

type PaymentTermValue = typeof PAYMENT_TERMS[number]['value'];

const applyPaymentTerm = (term: PaymentTermValue, base: Date): Date => {
  if (term === 'netMonthly') {
    const d = new Date(base);
    d.setMonth(d.getMonth() + 1, 0); // last day of current month
    return d;
  }
  const found = PAYMENT_TERMS.find(t => t.value === term);
  if (!found || found.days === null) return base; // custom → no-op
  return new Date(base.getTime() + found.days * 24 * 60 * 60 * 1000);
};

const getTermsText = (term: PaymentTermValue): string => {
  switch (term) {
    case 'net7':       return 'Payment is due within 7 days of the invoice date.';
    case 'net10':      return 'Payment is due within 10 days of the invoice date.';
    case 'net15':      return 'Payment is due within 15 days of the invoice date.';
    case 'net30':      return 'Payment is due within 30 days of the invoice date.';
    case 'net45':      return 'Payment is due within 45 days of the invoice date.';
    case 'net60':      return 'Payment is due within 60 days of the invoice date.';
    case 'net90':      return 'Payment is due within 90 days of the invoice date.';
    case 'netMonthly': return 'Payment is due by the end of the current month.';
    default:           return '';
  }
};
// ----------------------

interface AnonymousEditorProps {
  hideNoticeHeader?: boolean;
}

export function AnonymousEditor({ hideNoticeHeader = false }: AnonymousEditorProps) {
  const { toast } = useToast();

  // Mode: Invoice or Quotation
  const [mode, setMode] = useState<'invoice' | 'quotation'>('invoice');

  // Form tabs
  const [currentTab, setCurrentTab] = useState('basic');
  const [downloading, setDownloading] = useState(false);

  // Common details state
  const [businessName, setBusinessName] = useState('My Business Name');
  const [businessEmail, setBusinessEmail] = useState('billing@mycompany.com');
  const [businessPhone, setBusinessPhone] = useState('+1 (302) 555-0199');
  const [businessAddress, setBusinessAddress] = useState('1209 North Orange Street\nWilmington, DE 19801\nUnited States');

  const [clientName, setClientName] = useState('Acme Corporation LLC');
  const [clientEmail, setClientEmail] = useState('accounts@client.com');
  const [clientPhone, setClientPhone] = useState('+1 (650) 253-0000');
  const [clientAddress, setClientAddress] = useState('1600 Amphitheatre Parkway\nMountain View, CA 94043\nUnited States');

  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      description: 'Professional Web Design & Development Services',
      quantity: 1,
      rate: 3500,
      amount: 3500,
      lineTax: { type: 'none', value: 0, label: 'None', enabled: false },
      lineDiscount: { type: 'none', value: 0, enabled: false },
    },
    {
      description: 'Cloud Hosting Setup & Optimization',
      quantity: 1,
      rate: 750,
      amount: 750,
      lineTax: { type: 'none', value: 0, label: 'None', enabled: false },
      lineDiscount: { type: 'none', value: 0, enabled: false },
    }
  ]);

  const [taxes, setTaxes] = useState<Tax[]>([{ type: 'none', value: 0, label: 'Tax', enabled: false }]);
  const [discount, setDiscount] = useState<Discount>({ type: 'none', value: 0, label: 'Discount', enabled: false });
  const [currency, setCurrency] = useState<any>('USD');
  const [selectedTemplateId, setSelectedTemplateId] = useState<TemplateId>('none');
  const [colorTheme, setColorTheme] = useState<string>('slate');
  const [notes, setNotes] = useState('Thank you for choosing us! Payment is expected within terms.');
  const [terms, setTerms] = useState('Payment is due upon receipt of invoice.');

  // Invoice specific state
  const [invoiceNumber, setInvoiceNumber] = useState('INV-2026-0001');
  const [issueDate, setIssueDate] = useState<Date>(new Date());
  const [dueDate, setDueDate] = useState<Date>(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
  const [paymentTerm, setPaymentTerm] = useState<PaymentTermValue>('custom');

  // Quotation specific state
  const [quotationNumber, setQuotationNumber] = useState('QT-2026-0001');
  const [quotationIssueDate, setQuotationIssueDate] = useState<Date>(new Date());
  const [validUntil, setValidUntil] = useState<Date>(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
  const [prefix, setPrefix] = useState<'QT' | 'EST'>('QT');

  const [errors, setErrors] = useState<{
    businessEmail?: boolean;
    businessPhone?: boolean;
    clientEmail?: boolean;
    clientPhone?: boolean;
  }>({});

  // Number generation fallback simulation
  useEffect(() => {
    if (mode === 'quotation') {
      setQuotationNumber(`${prefix}-2026-0001`);
    }
  }, [prefix, mode]);

  // Load template from URL query params (e.g. ?template=midnight)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tplParam = params.get('template') || params.get('templateId');
    if (tplParam && INVOICE_TEMPLATES.some((t) => t.id === tplParam)) {
      setSelectedTemplateId(tplParam as TemplateId);
      const tpl = INVOICE_TEMPLATES.find((t) => t.id === tplParam);
      if (tpl) {
        toast({
          title: `Template Selected: ${tpl.name}`,
          description: `Loaded ${tpl.name} design into your invoice builder.`,
        });
      }
    }
  }, []);

  // Line item handlers
  const updateLineItemTax = (index: number, taxType: string, value: any) => {
    const updated = [...lineItems];
    const taxLabels: Record<string, string> = {
      'none': 'None',
      'vat': 'VAT',
      'sst': 'SST',
      'percentage': 'Tax',
      'fixed': 'Tax'
    };
    updated[index].lineTax = {
      type: taxType as any,
      value: value,
      label: taxLabels[taxType] || 'Tax',
      enabled: taxType !== 'none'
    };
    setLineItems(updated);
  };

  const updateLineItemDiscount = (index: number, discountType: string, value: any) => {
    const updated = [...lineItems];
    updated[index].lineDiscount = {
      type: discountType as any,
      value: value,
      enabled: discountType !== 'none'
    };
    setLineItems(updated);
  };

  const calculateLineItemTotal = (item: LineItem) => {
    const qty = Number(item.quantity) || 0;
    const rate = Number(item.rate) || 0;
    const baseAmount = qty * rate;
    let total = baseAmount;
    
    // Apply line tax
    if (item.lineTax && item.lineTax.type !== 'none') {
      const val = Number(item.lineTax.value) || 0;
      if (item.lineTax.type === 'percentage' || item.lineTax.type === 'vat' || item.lineTax.type === 'sst') {
        total += (baseAmount * val) / 100;
      } else if (item.lineTax.type === 'fixed') {
        total += val;
      }
    }
    
    // Apply line discount
    if (item.lineDiscount && item.lineDiscount.type !== 'none') {
      const val = Number(item.lineDiscount.value) || 0;
      if (item.lineDiscount.type === 'percentage') {
        total -= (baseAmount * val) / 100;
      } else if (item.lineDiscount.type === 'fixed') {
        total -= val;
      }
    }
    
    return total;
  };

  // Calculations
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + calculateLineItemTotal(item), 0);
  };

  const subtotal = calculateSubtotal();

  const calculateSingleTaxAmount = (t: Tax) => {
    if (!t.enabled || t.type === 'none') return 0;
    const val = Number(t.value) || 0;
    if (t.type === 'percentage' || t.type === 'vat' || t.type === 'sst') {
      return (subtotal * val) / 100;
    } else if (t.type === 'fixed') {
      return val;
    }
    return 0;
  };

  const taxAmount = taxes.reduce((acc, curr) => {
    if (!curr.enabled || curr.type === 'none') return acc;
    return acc + calculateSingleTaxAmount(curr);
  }, 0);

  const discountAmount = (() => {
    if (!discount.enabled || discount.type === 'none') return 0;
    const val = Number(discount.value) || 0;
    if (discount.type === 'percentage') {
      return (subtotal * val) / 100;
    } else if (discount.type === 'fixed') {
      return val;
    }
    return 0;
  })();

  const totalAmount = subtotal + taxAmount - discountAmount;

  const logAnonymousCreation = async (action: 'print' | 'download') => {
    if (!db) return;
    try {
      let visitorId = localStorage.getItem('visitor_id');
      if (!visitorId) {
        visitorId = 'visitor_' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem('visitor_id', visitorId);
      }

      const ua = navigator.userAgent;
      let browser = 'Unknown Browser';
      let os = 'Unknown OS';

      if (ua.indexOf('Win') !== -1) os = 'Windows';
      else if (ua.indexOf('Mac') !== -1) os = 'MacOS';
      else if (ua.indexOf('X11') !== -1) os = 'UNIX';
      else if (ua.indexOf('Linux') !== -1) os = 'Linux';
      else if (/Android/.test(ua)) os = 'Android';
      else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';

      if (ua.indexOf('Chrome') !== -1 && ua.indexOf('Safari') !== -1) browser = 'Chrome';
      else if (ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1) browser = 'Safari';
      else if (ua.indexOf('Firefox') !== -1) browser = 'Firefox';
      else if (ua.indexOf('MSIE') !== -1 || ua.indexOf('Trident') !== -1) browser = 'IE';
      else if (ua.indexOf('Edge') !== -1 || ua.indexOf('Edg') !== -1) browser = 'Edge';

      const country = sessionStorage.getItem('visitor_country') || 'Unknown Country';

      await addDoc(collection(db, 'anonymous_creations'), {
        visitorId,
        type: mode,
        action,
        currency,
        totalAmount: Number(totalAmount) || 0,
        browser,
        os,
        country,
        timestamp: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error logging anonymous creation:', err);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      lineTax: { type: 'none', value: 0, label: 'None', enabled: false },
      lineDiscount: { type: 'none', value: 0, enabled: false }
    }]);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'quantity' || field === 'rate') {
      const qty = Number(updated[index].quantity) || 0;
      const rate = Number(updated[index].rate) || 0;
      updated[index].amount = qty * rate;
    }
    setLineItems(updated);
  };

  const handlePrint = () => {
    window.print();
    logAnonymousCreation('print');
  };

  const handleDownloadPDF = async () => {
    // Validate email and phone
    const newErrors: typeof errors = {};
    let firstErrorTab: string | null = null;

    if (businessEmail && !isValidEmail(businessEmail)) {
      newErrors.businessEmail = true;
      if (!firstErrorTab) firstErrorTab = 'basic';
    }

    if (businessPhone && !isValidPhone(businessPhone)) {
      newErrors.businessPhone = true;
      if (!firstErrorTab) firstErrorTab = 'basic';
    }

    if (clientEmail && !isValidEmail(clientEmail)) {
      newErrors.clientEmail = true;
      if (!firstErrorTab) firstErrorTab = 'client';
    }

    if (clientPhone && !isValidPhone(clientPhone)) {
      newErrors.clientPhone = true;
      if (!firstErrorTab) firstErrorTab = 'client';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (firstErrorTab) {
        setCurrentTab(firstErrorTab);
      }
      toast({
        title: 'Validation Error',
        description: 'Please correct the highlighted fields in the form.',
        variant: 'destructive',
      });
      return;
    }

    setErrors({});
    setDownloading(true);
    try {
      if (mode === 'invoice') {
        await exportInvoiceToPDF(invoiceData, 'try-invoice-content');
      } else {
        await exportQuotationToPDF(quotationData, 'try-quotation-content');
      }
      logAnonymousCreation('download');
      toast({ title: 'Success', description: 'Document downloaded successfully!' });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  // Construct print-compatible structures
  const invoiceData: any = {
    invoiceNumber,
    issueDate,
    dueDate,
    currency,
    templateId: selectedTemplateId,
    colorTheme: colorTheme,
    subtotal,
    totalDiscount: discountAmount,
    totalTax: taxAmount,
    totalAmount,
    notes,
    terms,
    from: {
      name: businessName,
      email: businessEmail,
      phone: businessPhone,
      address: businessAddress,
      logoUrl: "",
    },
    billTo: {
      name: clientName,
      email: clientEmail,
      phone: clientPhone,
      address: clientAddress,
    },
    lineItems: lineItems.map((item) => ({
      description: item.description,
      quantity: Number(item.quantity) || 0,
      unitPrice: Number(item.rate) || 0,
      rate: Number(item.rate) || 0,
      subtotal: (Number(item.quantity) || 0) * (Number(item.rate) || 0),
      amount: (Number(item.quantity) || 0) * (Number(item.rate) || 0),
      lineTax: {
        enabled: item.lineTax?.type !== 'none',
        type: item.lineTax?.type || 'none',
        label: item.lineTax?.label || '',
        value: Number(item.lineTax?.value) || 0,
      },
      lineDiscount: {
        enabled: item.lineDiscount?.type !== 'none',
        type: item.lineDiscount?.type || 'none',
        value: Number(item.lineDiscount?.value) || 0,
      },
    })),
    settings: {
      headerTax: {
        enabled: taxes.some(t => t.type !== 'none' && t.enabled),
        taxes: taxes.map(t => ({
          label: t.label || 'Tax',
          type: t.type,
          value: Number(t.value) || 0,
          enabled: t.enabled
        })),
      },
      headerDiscount: {
        type: discount.type,
        value: Number(discount.value) || 0,
        label: discount.label || 'Discount',
        enabled: discount.enabled
      },
    }
  };

  const quotationData: any = {
    quotationNumber,
    issueDate: quotationIssueDate,
    validUntil,
    currency,
    templateId: selectedTemplateId,
    colorTheme: colorTheme,
    subtotal,
    totalDiscount: discountAmount,
    totalTax: taxAmount,
    totalAmount,
    notes,
    terms,
    from: {
      name: businessName,
      email: businessEmail,
      phone: businessPhone,
      address: businessAddress,
      logoUrl: "",
    },
    billTo: {
      name: clientName,
      email: clientEmail,
      phone: clientPhone,
      address: clientAddress,
    },
    lineItems: lineItems.map((item) => ({
      description: item.description,
      quantity: Number(item.quantity) || 0,
      unitPrice: Number(item.rate) || 0,
      rate: Number(item.rate) || 0,
      subtotal: (Number(item.quantity) || 0) * (Number(item.rate) || 0),
      amount: (Number(item.quantity) || 0) * (Number(item.rate) || 0),
      lineTax: {
        enabled: item.lineTax?.type !== 'none',
        type: item.lineTax?.type || 'none',
        label: item.lineTax?.label || '',
        value: Number(item.lineTax?.value) || 0,
      },
      lineDiscount: {
        enabled: item.lineDiscount?.type !== 'none',
        type: item.lineDiscount?.type || 'none',
        value: Number(item.lineDiscount?.value) || 0,
      },
    })),
    settings: {
      headerTax: {
        enabled: taxes.some(t => t.type !== 'none' && t.enabled),
        taxes: taxes.map(t => ({
          label: t.label || 'Tax',
          type: t.type,
          value: Number(t.value) || 0,
          enabled: t.enabled
        })),
      },
      headerDiscount: {
        type: discount.type,
        value: Number(discount.value) || 0,
        label: discount.label || 'Discount',
        enabled: discount.enabled
      },
    }
  };

  const handleClearData = () => {
    if (!window.confirm("Are you sure you want to clear all invoice/quotation details? This action cannot be undone.")) {
      return;
    }
    
    // Reset business info
    setBusinessName('');
    setBusinessEmail('');
    setBusinessPhone('');
    setBusinessAddress('');
    
    // Reset client info
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    setClientAddress('');
    
    // Reset document numbers
    setInvoiceNumber('');
    setQuotationNumber('');
    
    // Reset basic line items - keep 1 empty template row to preserve structural bounds
    setLineItems([{
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      lineTax: { type: 'none', value: 0, label: 'None', enabled: false },
      lineDiscount: { type: 'none', value: 0, enabled: false },
    }]);
    
    // Reset extra metrics
    setTaxes([{ type: 'none', value: 0, label: 'Tax', enabled: false }]);
    setDiscount({ type: 'none', value: 0, label: 'Discount', enabled: false });
    
    // Reset text notes
    setNotes('');
    setTerms('');
    
    // Reset payment terms to custom
    setPaymentTerm('custom');
    setColorTheme('slate');

    toast({
      title: 'Data Cleared',
      description: 'The editor workspace has been reset successfully.',
    });
  };

  return (
    <div className="bg-gray-50 text-left print:bg-transparent print:p-0 print:m-0">
      {/* Notice header - hidden when printing */}
      {!hideNoticeHeader && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 print:hidden text-center text-xs md:text-sm font-semibold flex items-center justify-center gap-3">
          <span>Drafting anonymously. Sign up for a free account to save and manage documents in the cloud!</span>
          <Link to="/register">
            <Button size="sm" className="bg-white text-indigo-700 hover:bg-slate-100 font-bold h-7 py-0 px-3 text-xs">
              Sign Up Free
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 print:p-0 print:m-0 print:max-w-none">
        {/* Title and main mode switcher */}
        <div className={cn("flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4 print:hidden", hideNoticeHeader && "md:justify-center")}>
          {!hideNoticeHeader && (
            <div>
              <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">Try It Out</h1>
              <p className="text-sm text-slate-500 mt-1">Design and test invoice and quotation templates instantly on-the-fly.</p>
            </div>
          )}
          
          <div className="flex bg-slate-200/60 p-1.5 rounded-xl border border-slate-200/65 gap-1 shadow-inner">
            <Button
              variant={mode === 'invoice' ? 'default' : 'ghost'}
              onClick={() => {
                setMode('invoice');
                setCurrentTab('basic');
              }}
              className={cn(
                "font-bold text-xs md:text-sm px-5 py-1.5 h-9 rounded-lg transition-all duration-200",
                mode === 'invoice' 
                  ? "bg-white text-slate-900 shadow-sm border border-slate-200/30 hover:bg-white" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              )}
            >
              Invoice Mode
            </Button>
            <Button
              variant={mode === 'quotation' ? 'default' : 'ghost'}
              onClick={() => {
                setMode('quotation');
                setCurrentTab('basic');
              }}
              className={cn(
                "font-bold text-xs md:text-sm px-5 py-1.5 h-9 rounded-lg transition-all duration-200",
                mode === 'quotation' 
                  ? "bg-white text-slate-900 shadow-sm border border-slate-200/30 hover:bg-white" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              )}
            >
              Quotation Mode
            </Button>
          </div>
        </div>

        {/* Outer Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start print:block print:w-full print:m-0 print:p-0 print:gap-0">
          
          {/* Left panel - Form Controls */}
          <div className="lg:col-span-5 space-y-6 print:hidden">
            <Card className="border-slate-100 shadow-sm">
              <CardHeader className="p-5 border-b border-slate-50">
                <CardTitle className="text-lg font-bold text-slate-800">
                  {mode === 'invoice' ? 'Invoice Details' : 'Quotation Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <Tabs value={currentTab} onValueChange={setCurrentTab}>
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="basic" className="flex items-center gap-1 text-xs">
                      Basic
                      {(errors.businessEmail || errors.businessPhone) && (
                        <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="client" className="flex items-center gap-1 text-xs">
                      Client
                      {(errors.clientEmail || errors.clientPhone) && (
                        <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="items" className="text-xs">Items</TabsTrigger>
                    <TabsTrigger value="design" className="text-xs">Design</TabsTrigger>
                  </TabsList>

                  {/* Basic Details Tab */}
                  <TabsContent value="basic" className="space-y-4 mt-4">
                    {mode === 'invoice' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="invoice-number">Invoice Number</Label>
                          <Input
                            id="invoice-number"
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            placeholder="INV-2026-0001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="issue-date-picker">Issue Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button id="issue-date-picker" variant="outline" className="w-full justify-start text-left font-normal text-xs">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(issueDate, 'PPP')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar mode="single" selected={issueDate} onSelect={(date) => {
                                if (date) {
                                  setIssueDate(date);
                                  if (paymentTerm !== 'custom') {
                                    setDueDate(applyPaymentTerm(paymentTerm, date));
                                  }
                                }
                              }} />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="payment-terms-select">Payment Terms</Label>
                          <Select
                            value={paymentTerm}
                            onValueChange={(val: PaymentTermValue) => {
                              setPaymentTerm(val);
                              if (val !== 'custom') {
                                setDueDate(applyPaymentTerm(val, issueDate));
                                setTerms(getTermsText(val));
                              }
                            }}
                          >
                            <SelectTrigger id="payment-terms-select" className="w-full" aria-label="Select Payment Terms">
                              <SelectValue placeholder="Select payment terms" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {PAYMENT_TERMS.map((term) => (
                                <SelectItem key={term.value} value={term.value}>
                                  <div className="flex flex-col text-left">
                                    <span className="font-semibold text-xs">{term.label}</span>
                                    {term.bestFor && (
                                      <span className="text-[10px] text-muted-foreground select-description">{term.description} • {term.bestFor}</span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="due-date-picker">Due Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button id="due-date-picker" variant="outline" className="w-full justify-start text-left font-normal text-xs">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(dueDate, 'PPP')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar mode="single" selected={dueDate} onSelect={(date) => {
                                if (date) {
                                  setDueDate(date);
                                  setPaymentTerm('custom');
                                }
                              }} />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="document-prefix-select">Document Prefix</Label>
                          <Select value={prefix} onValueChange={(value: 'QT' | 'EST') => setPrefix(value)}>
                            <SelectTrigger id="document-prefix-select" aria-label="Select Document Prefix">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="QT">Quotation (QT)</SelectItem>
                              <SelectItem value="EST">Estimate (EST)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="quotation-number">Quotation Number</Label>
                          <Input
                            id="quotation-number"
                            value={quotationNumber}
                            onChange={(e) => setQuotationNumber(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Issue Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal text-xs">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(quotationIssueDate, 'PPP')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar mode="single" selected={quotationIssueDate} onSelect={(date) => date && setQuotationIssueDate(date)} />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label>Valid Until</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-left font-normal text-xs">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(validUntil, 'PPP')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar mode="single" selected={validUntil} onSelect={(date) => date && setValidUntil(date)} />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    )}

                    {/* Business Details */}
                    <div className="pt-4 border-t space-y-4">
                      <Label className="text-sm font-bold text-slate-800">Your Business Details</Label>

                      <div className="space-y-2">
                        <Label>Business Name</Label>
                        <Input
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          placeholder="Your Company Name"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Business Email</Label>
                          <Input
                            type="email"
                            value={businessEmail}
                            onChange={(e) => {
                              setBusinessEmail(e.target.value);
                              if (errors.businessEmail) setErrors(prev => ({ ...prev, businessEmail: false }));
                            }}
                            className={cn(errors.businessEmail && "border-red-500 focus-visible:ring-red-500")}
                            placeholder="billing@mycompany.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Business Phone</Label>
                          <Input
                            value={businessPhone}
                            onChange={(e) => {
                              setBusinessPhone(e.target.value);
                              if (errors.businessPhone) setErrors(prev => ({ ...prev, businessPhone: false }));
                            }}
                            className={cn(errors.businessPhone && "border-red-500 focus-visible:ring-red-500")}
                            placeholder="+60 12-345 6789"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Business Address</Label>
                        <Textarea
                          value={businessAddress}
                          onChange={(e) => setBusinessAddress(e.target.value)}
                          placeholder="Street name, City, State, ZIP"
                          rows={3}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 mt-6">
                      <Button type="button" onClick={() => setCurrentTab('client')}>
                        Next: Client Details
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Client Details Tab */}
                  <TabsContent value="client" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Client Name</Label>
                      <Input
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Client Company Name"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Client Email</Label>
                        <Input
                          type="email"
                          value={clientEmail}
                          onChange={(e) => {
                            setClientEmail(e.target.value);
                            if (errors.clientEmail) setErrors(prev => ({ ...prev, clientEmail: false }));
                          }}
                          className={cn(errors.clientEmail && "border-red-500 focus-visible:ring-red-500")}
                          placeholder="client@company.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Client Phone</Label>
                        <Input
                          value={clientPhone}
                          onChange={(e) => {
                            setClientPhone(e.target.value);
                            if (errors.clientPhone) setErrors(prev => ({ ...prev, clientPhone: false }));
                          }}
                          className={cn(errors.clientPhone && "border-red-500 focus-visible:ring-red-500")}
                          placeholder="+60 3-9876 5432"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Client Address</Label>
                      <Textarea
                        value={clientAddress}
                        onChange={(e) => setClientAddress(e.target.value)}
                        placeholder="Client Address"
                        rows={3}
                      />
                    </div>

                    <div className="flex justify-end pt-4 mt-6">
                      <Button type="button" onClick={() => setCurrentTab('items')}>
                        Next: Line Items
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Line Items Tab */}
                  <TabsContent value="items" className="space-y-4 mt-4">
                    {lineItems.map((item, index) => (
                      <Card key={index} className="border-slate-100/80 bg-slate-50/20">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex justify-between items-center">
                            <Label className="text-xs font-bold text-slate-500">Item {index + 1}</Label>
                            {lineItems.length > 1 && (
                              <Button variant="ghost" size="sm" onClick={() => removeLineItem(index)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700">
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-xs">Description</Label>
                            <Input
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                              placeholder="Item description"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label className="text-xs">Qty</Label>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                                min="1"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs">Rate</Label>
                              <Input
                                type="number"
                                value={item.rate}
                                onChange={(e) => updateLineItem(index, 'rate', e.target.value)}
                                min="0"
                              />
                            </div>
                          </div>

                          {/* Line Item Tax */}
                          <div className="space-y-2 pt-2 border-t mt-2">
                            <Label className="text-xs font-medium">Tax (per item)</Label>
                            <div className="flex gap-2">
                              <Select 
                                value={item.lineTax?.type || 'none'} 
                                onValueChange={(value) => updateLineItemTax(index, value, item.lineTax?.value || 0)}
                              >
                                <SelectTrigger className="w-28 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  <SelectItem value="vat">VAT %</SelectItem>
                                  <SelectItem value="sst">SST %</SelectItem>
                                  <SelectItem value="percentage">Tax %</SelectItem>
                                  <SelectItem value="fixed">Fixed</SelectItem>
                                </SelectContent>
                              </Select>
                              {item.lineTax?.type !== 'none' && (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="h-8 text-xs"
                                  placeholder={item.lineTax?.type === 'fixed' ? 'Amount' : 'Percentage'}
                                  value={item.lineTax?.value ?? ''}
                                  onChange={(e) => updateLineItemTax(index, item.lineTax?.type || 'none', e.target.value)}
                                />
                              )}
                            </div>
                          </div>

                          {/* Line Item Discount */}
                          <div className="space-y-2 mt-2">
                            <Label className="text-xs font-medium">Discount (per item)</Label>
                            <div className="flex gap-2">
                              <Select 
                                value={item.lineDiscount?.type || 'none'} 
                                onValueChange={(value) => updateLineItemDiscount(index, value, item.lineDiscount?.value || 0)}
                              >
                                <SelectTrigger className="w-28 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  <SelectItem value="percentage">%</SelectItem>
                                  <SelectItem value="fixed">Fixed</SelectItem>
                                </SelectContent>
                              </Select>
                              {item.lineDiscount?.type !== 'none' && (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="h-8 text-xs"
                                  placeholder={item.lineDiscount?.type === 'fixed' ? 'Amount' : 'Percentage'}
                                  value={item.lineDiscount?.value ?? ''}
                                  onChange={(e) => updateLineItemDiscount(index, item.lineDiscount?.type || 'none', e.target.value)}
                                />
                              )}
                            </div>
                          </div>

                          {/* Line Item Total */}
                          <div className="flex justify-between pt-2 border-t font-medium text-xs mt-2">
                            <span>Line Total:</span>
                            <span>{getCurrencySymbol(currency)}{calculateLineItemTotal(item).toFixed(2)}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    <Button type="button" variant="outline" className="w-full" onClick={addLineItem}>
                      <Plus className="h-4 w-4 mr-2" /> Add Line Item
                    </Button>

                    {/* Global Tax and Discount Settings */}
                    <div className="pt-4 border-t space-y-4">
                      {/* Taxes Section */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-sm font-semibold">Taxes</Label>
                          <div className="flex gap-2">
                            <Select
                              onValueChange={(valIdx) => {
                                const preTax = COMMON_TAXES[Number(valIdx)];
                                if (preTax) {
                                  const exists = taxes.some(t => t.type === preTax.type && t.value === preTax.value);
                                  if (!exists) {
                                    const updated = taxes.filter(t => t.type !== 'none');
                                    updated.push({
                                      type: preTax.type,
                                      value: preTax.value,
                                      label: preTax.label.split(' ')[0] + ' ' + (preTax.label.split(' ')[1] || ''),
                                      enabled: true
                                    });
                                    setTaxes(updated.length > 0 ? updated : [{ type: 'none', value: 0, label: 'Tax', enabled: false }]);
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="w-36 h-7 text-xs">
                                <SelectValue placeholder="Quick Add Tax" />
                              </SelectTrigger>
                              <SelectContent>
                                {COMMON_TAXES.map((tax, idx) => (
                                  <SelectItem key={idx} value={idx.toString()} className="text-xs">
                                    {tax.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {taxes.every(t => t.type !== 'none') && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setTaxes([...taxes, { type: 'none', value: 0, label: 'Tax', enabled: false }]);
                                }}
                                className="h-7 text-xs flex items-center gap-1"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Custom
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-3">
                          {taxes.map((t, index) => (
                            <div key={index} className="space-y-1 bg-slate-50/50 p-2.5 rounded-lg border border-slate-100 animate-in fade-in duration-200">
                              <div className="flex gap-2 items-center">
                                <Select
                                  value={t.type}
                                  onValueChange={(value: any) => {
                                    const labels: Record<string, string> = {
                                      'none': 'Tax',
                                      'vat': 'VAT',
                                      'sst': 'SST',
                                      'percentage': 'Sales Tax',
                                      'fixed': 'Tax'
                                    };
                                    const updated = [...taxes];
                                    updated[index] = {
                                      ...updated[index],
                                      type: value,
                                      label: labels[value] || 'Tax',
                                      enabled: value !== 'none'
                                    };
                                    setTaxes(updated);
                                  }}
                                >
                                  <SelectTrigger className="w-28 h-8 text-xs shrink-0">
                                    <SelectValue placeholder="Tax Type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    <SelectItem value="vat">VAT %</SelectItem>
                                    <SelectItem value="sst">SST %</SelectItem>
                                    <SelectItem value="percentage">Sales Tax %</SelectItem>
                                    <SelectItem value="fixed">Fixed</SelectItem>
                                  </SelectContent>
                                </Select>

                                {t.type !== 'none' && (
                                  <div className="relative flex-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={t.value ?? ''}
                                      onChange={(e) => {
                                        const updated = [...taxes];
                                        updated[index] = {
                                          ...updated[index],
                                          value: e.target.value
                                        };
                                        setTaxes(updated);
                                      }}
                                      className="h-8 text-xs pr-8"
                                      placeholder="0.00"
                                    />
                                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-medium pointer-events-none">
                                      {t.type === 'fixed' ? getCurrencySymbol(currency) : '%'}
                                    </span>
                                  </div>
                                )}

                                {index > 0 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      const updated = taxes.filter((_, i) => i !== index);
                                      setTaxes(updated);
                                    }}
                                    className="h-8 w-8 text-red-500 hover:text-red-700 shrink-0"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                              {t.type !== 'none' && (
                                <div className="flex justify-between text-[11px] text-slate-500 px-1 pt-1">
                                  <span>{t.label || 'Tax'} Amount:</span>
                                  <span>{getCurrencySymbol(currency)}{calculateSingleTaxAmount(t).toFixed(2)}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Discount Section */}
                      <div className="space-y-2 pt-2 border-t">
                        <Label className="text-sm font-semibold">Discount</Label>
                        <div className="flex gap-2">
                          <Select
                            value={discount.type}
                            onValueChange={(val: any) => setDiscount(prev => ({ ...prev, type: val, enabled: val !== 'none' }))}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="percentage">% (Percentage)</SelectItem>
                              <SelectItem value="fixed">Fixed Amount</SelectItem>
                            </SelectContent>
                          </Select>
                          {discount.type !== 'none' && (
                            <div className="relative flex-1">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={discount.value ?? ''}
                                onChange={(e) => setDiscount(prev => ({ ...prev, value: e.target.value }))}
                                className="h-8 text-xs pr-8"
                                placeholder="0.00"
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-medium pointer-events-none">
                                {discount.type === 'fixed' ? getCurrencySymbol(currency) : '%'}
                              </span>
                            </div>
                          )}
                        </div>
                        {discount.type !== 'none' && (
                          <div className="flex justify-between text-[11px] text-slate-500 px-1 pt-0.5">
                            <span>Discount Amount:</span>
                            <span>{getCurrencySymbol(currency)}{discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 mt-6">
                      <Button type="button" onClick={() => setCurrentTab('design')}>
                        Next: Template Design
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Summary & Design Tab */}
                  <TabsContent value="design" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select value={currency} onValueChange={(val: any) => setCurrency(val)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="USD">USD ($) - US Dollar</SelectItem>
                          <SelectItem value="EUR">EUR (€) - Euro</SelectItem>
                          <SelectItem value="GBP">GBP (£) - British Pound</SelectItem>
                          <SelectItem value="JPY">JPY (¥) - Japanese Yen</SelectItem>
                          <SelectItem value="CNY">CNY (¥) - Chinese Yuan</SelectItem>
                          <SelectItem value="AUD">AUD (A$) - Australian Dollar</SelectItem>
                          <SelectItem value="CAD">CAD (C$) - Canadian Dollar</SelectItem>
                          <SelectItem value="CHF">CHF (Fr) - Swiss Franc</SelectItem>
                          <SelectItem value="INR">INR (₹) - Indian Rupee</SelectItem>
                          <SelectItem value="MYR">MYR (RM) - Malaysian Ringgit</SelectItem>
                          <SelectItem value="SGD">SGD (S$) - Singapore Dollar</SelectItem>
                          <SelectItem value="NZD">NZD (NZ$) - New Zealand Dollar</SelectItem>
                          <SelectItem value="AED">AED (د.إ) - UAE Dirham</SelectItem>
                          <SelectItem value="ARS">ARS ($) - Argentine Peso</SelectItem>
                          <SelectItem value="AFN">AFN (؋) - Afghan Afghani</SelectItem>
                          <SelectItem value="ALL">ALL (L) - Albanian Lek</SelectItem>
                          <SelectItem value="AMD">AMD (֏) - Armenian Dram</SelectItem>
                          <SelectItem value="ANG">ANG (ƒ) - Netherlands Antillean Guilder</SelectItem>
                          <SelectItem value="AOA">AOA (Kz) - Angolan Kwanza</SelectItem>
                          <SelectItem value="AWG">AWG (ƒ) - Aruban Florin</SelectItem>
                          <SelectItem value="AZN">AZN (₼) - Azerbaijani Manat</SelectItem>
                          <SelectItem value="BAM">BAM (KM) - Bosnia-Herzegovina Convertible Mark</SelectItem>
                          <SelectItem value="BBD">BBD ($) - Barbadian Dollar</SelectItem>
                          <SelectItem value="BDT">BDT (৳) - Bangladeshi Taka</SelectItem>
                          <SelectItem value="BGN">BGN (лв) - Bulgarian Lev</SelectItem>
                          <SelectItem value="BHD">BHD (.د.ب) - Bahraini Dinar</SelectItem>
                          <SelectItem value="BIF">BIF (Fr) - Burundian Franc</SelectItem>
                          <SelectItem value="BMD">BMD ($) - Bermudan Dollar</SelectItem>
                          <SelectItem value="BND">BND ($) - Brunei Dollar</SelectItem>
                          <SelectItem value="BOB">BOB (Bs.) - Bolivian Boliviano</SelectItem>
                          <SelectItem value="BRL">BRL (R$) - Brazilian Real</SelectItem>
                          <SelectItem value="BSD">BSD ($) - Bahamian Dollar</SelectItem>
                          <SelectItem value="BTN">BTN (Nu.) - Bhutanese Ngultrum</SelectItem>
                          <SelectItem value="BWP">BWP (P) - Botswanan Pula</SelectItem>
                          <SelectItem value="BYN">BYN (Br) - Belarusian Ruble</SelectItem>
                          <SelectItem value="BZD">BZD ($) - Belize Dollar</SelectItem>
                          <SelectItem value="CDF">CDF (Fr) - Congolese Franc</SelectItem>
                          <SelectItem value="CLP">CLP ($) - Chilean Peso</SelectItem>
                          <SelectItem value="COP">COP ($) - Colombian Peso</SelectItem>
                          <SelectItem value="CRC">CRC (₡) - Costa Rican Colón</SelectItem>
                          <SelectItem value="CUP">CUP ($) - Cuban Peso</SelectItem>
                          <SelectItem value="CVE">CVE ($) - Cape Verdean Escudo</SelectItem>
                          <SelectItem value="CZK">CZK (Kč) - Czech Koruna</SelectItem>
                          <SelectItem value="DJF">DJF (Fr) - Djiboutian Franc</SelectItem>
                          <SelectItem value="DKK">DKK (kr) - Danish Krone</SelectItem>
                          <SelectItem value="DOP">DOP ($) - Dominican Peso</SelectItem>
                          <SelectItem value="DZD">DZD (د.ج) - Algerian Dinar</SelectItem>
                          <SelectItem value="EGP">EGP (£) - Egyptian Pound</SelectItem>
                          <SelectItem value="ERN">ERN (Nfk) - Eritrean Nakfa</SelectItem>
                          <SelectItem value="ETB">ETB (Br) - Ethiopian Birr</SelectItem>
                          <SelectItem value="FJD">FJD ($) - Fijian Dollar</SelectItem>
                          <SelectItem value="FKP">FKP (£) - Falkland Islands Pound</SelectItem>
                          <SelectItem value="FOK">FOK (kr) - Faroese Króna</SelectItem>
                          <SelectItem value="GEL">GEL (₾) - Georgian Lari</SelectItem>
                          <SelectItem value="GGP">GGP (£) - Guernsey Pound</SelectItem>
                          <SelectItem value="GHS">GHS (₵) - Ghanaian Cedi</SelectItem>
                          <SelectItem value="GIP">GIP (£) - Gibraltar Pound</SelectItem>
                          <SelectItem value="GMD">GMD (D) - Gambian Dalasi</SelectItem>
                          <SelectItem value="GNF">GNF (Fr) - Guinean Franc</SelectItem>
                          <SelectItem value="GTQ">GTQ (Q) - Guatemalan Quetzal</SelectItem>
                          <SelectItem value="GYD">GYD ($) - Guyanaese Dollar</SelectItem>
                          <SelectItem value="HKD">HKD ($) - Hong Kong Dollar</SelectItem>
                          <SelectItem value="HNL">HNL (L) - Honduran Lempira</SelectItem>
                          <SelectItem value="HRK">HRK (kn) - Croatian Kuna</SelectItem>
                          <SelectItem value="HTG">HTG (G) - Haitian Gourde</SelectItem>
                          <SelectItem value="HUF">HUF (Ft) - Hungarian Forint</SelectItem>
                          <SelectItem value="IDR">IDR (Rp) - Indonesian Rupiah</SelectItem>
                          <SelectItem value="ILS">ILS (₪) - Israeli New Shekel</SelectItem>
                          <SelectItem value="IMP">IMP (£) - Isle of Man Pound</SelectItem>
                          <SelectItem value="IQD">IQD (ع.د) - Iraqi Dinar</SelectItem>
                          <SelectItem value="IRR">IRR (﷼) - Iranian Rial</SelectItem>
                          <SelectItem value="ISK">ISK (kr) - Icelandic Króna</SelectItem>
                          <SelectItem value="JEP">JEP (£) - Jersey Pound</SelectItem>
                          <SelectItem value="JMD">JMD ($) - Jamaican Dollar</SelectItem>
                          <SelectItem value="JOD">JOD (د.ا) - Jordanian Dinar</SelectItem>
                          <SelectItem value="KES">KES (Sh) - Kenyan Shilling</SelectItem>
                          <SelectItem value="KGS">KGS (с) - Kyrgystani Som</SelectItem>
                          <SelectItem value="KHR">KHR (៛) - Cambodian Riel</SelectItem>
                          <SelectItem value="KID">KID ($) - Kiribati Dollar</SelectItem>
                          <SelectItem value="KMF">KMF (Fr) - Comorian Franc</SelectItem>
                          <SelectItem value="KRW">KRW (₩) - South Korean Won</SelectItem>
                          <SelectItem value="KWD">KWD (د.ك) - Kuwaiti Dinar</SelectItem>
                          <SelectItem value="KYD">KYD ($) - Cayman Islands Dollar</SelectItem>
                          <SelectItem value="KZT">KZT (₸) - Kazakhstani Tenge</SelectItem>
                          <SelectItem value="LAK">LAK (₭) - Laotian Kip</SelectItem>
                          <SelectItem value="LBP">LBP (ل.ل) - Lebanese Pound</SelectItem>
                          <SelectItem value="LKR">LKR (Rs) - Sri Lankan Rupee</SelectItem>
                          <SelectItem value="LRD">LRD ($) - Liberian Dollar</SelectItem>
                          <SelectItem value="LSL">LSL (L) - Lesotho Loti</SelectItem>
                          <SelectItem value="LYD">LYD (ل.د) - Libyan Dinar</SelectItem>
                          <SelectItem value="MAD">MAD (د.م.) - Moroccan Dirham</SelectItem>
                          <SelectItem value="MDL">MDL (L) - Moldovan Leu</SelectItem>
                          <SelectItem value="MGA">MGA (Ar) - Malagasy Ariary</SelectItem>
                          <SelectItem value="MKD">MKD (ден) - Macedonian Denar</SelectItem>
                          <SelectItem value="MMK">MMK (Ks) - Myanmar Kyat</SelectItem>
                          <SelectItem value="MNT">MNT (₮) - Mongolian Tugrik</SelectItem>
                          <SelectItem value="MOP">MOP (P) - Macanese Pataca</SelectItem>
                          <SelectItem value="MRU">MRU (UM) - Mauritanian Ouguiya</SelectItem>
                          <SelectItem value="MUR">MUR (₨) - Mauritian Rupee</SelectItem>
                          <SelectItem value="MVR">MVR (ރ.) - Maldivian Rufiyaa</SelectItem>
                          <SelectItem value="MWK">MWK (MK) - Malawian Kwacha</SelectItem>
                          <SelectItem value="MXN">MXN ($) - Mexican Peso</SelectItem>
                          <SelectItem value="MZN">MZN (MT) - Mozambican Metical</SelectItem>
                          <SelectItem value="NAD">NAD ($) - Namibian Dollar</SelectItem>
                          <SelectItem value="NGN">NGN (₦) - Nigerian Naira</SelectItem>
                          <SelectItem value="NIO">NIO (C$) - Nicaraguan Córdoba</SelectItem>
                          <SelectItem value="NOK">NOK (kr) - Norwegian Krone</SelectItem>
                          <SelectItem value="NPR">NPR (₨) - Nepalese Rupee</SelectItem>
                          <SelectItem value="OMR">OMR (ر.ع.) - Omani Rial</SelectItem>
                          <SelectItem value="PAB">PAB (B/.) - Panamanian Balboa</SelectItem>
                          <SelectItem value="PEN">PEN (S/.) - Peruvian Sol</SelectItem>
                          <SelectItem value="PGK">PGK (K) - Papua New Guinean Kina</SelectItem>
                          <SelectItem value="PHP">PHP (₱) - Philippine Peso</SelectItem>
                          <SelectItem value="PKR">PKR (₨) - Pakistani Rupee</SelectItem>
                          <SelectItem value="PLN">PLN (zł) - Polish Zloty</SelectItem>
                          <SelectItem value="PYG">PYG (₲) - Paraguayan Guarani</SelectItem>
                          <SelectItem value="QAR">QAR (ر.ق) - Qatari Rial</SelectItem>
                          <SelectItem value="RON">RON (lei) - Romanian Leu</SelectItem>
                          <SelectItem value="RSD">RSD (дин.) - Serbian Dinar</SelectItem>
                          <SelectItem value="RUB">RUB (₽) - Russian Ruble</SelectItem>
                          <SelectItem value="RWF">RWF (Fr) - Rwandan Franc</SelectItem>
                          <SelectItem value="SAR">SAR (ر.س) - Saudi Riyal</SelectItem>
                          <SelectItem value="SBD">SBD ($) - Solomon Islands Dollar</SelectItem>
                          <SelectItem value="SCR">SCR (₨) - Seychellois Rupee</SelectItem>
                          <SelectItem value="SDG">SDG (ج.س.) - Sudanese Pound</SelectItem>
                          <SelectItem value="SEK">SEK (kr) - Swedish Krona</SelectItem>
                          <SelectItem value="SHP">SHP (£) - Saint Helena Pound</SelectItem>
                          <SelectItem value="SLE">SLE (Le) - Sierra Leonean Leone</SelectItem>
                          <SelectItem value="SOS">SOS (Sh) - Somali Shilling</SelectItem>
                          <SelectItem value="SRD">SRD ($) - Surinamese Dollar</SelectItem>
                          <SelectItem value="SSP">SSP (£) - South Sudanese Pound</SelectItem>
                          <SelectItem value="STN">STN (Db) - São Tomé and Príncipe Dobra</SelectItem>
                          <SelectItem value="SYP">SYP (£) - Syrian Pound</SelectItem>
                          <SelectItem value="SZL">SZL (L) - Swazi Lilangeni</SelectItem>
                          <SelectItem value="THB">THB (฿) - Thai Baht</SelectItem>
                          <SelectItem value="TJS">TJS (ЅМ) - Tajikistani Somoni</SelectItem>
                          <SelectItem value="TMT">TMT (m) - Turkmenistani Manat</SelectItem>
                          <SelectItem value="TND">TND (د.ت) - Tunisian Dinar</SelectItem>
                          <SelectItem value="TOP">TOP (T$) - Tongan Paʻanga</SelectItem>
                          <SelectItem value="TRY">TRY (₺) - Turkish Lira</SelectItem>
                          <SelectItem value="TTD">TTD ($) - Trinidad and Tobago Dollar</SelectItem>
                          <SelectItem value="TVD">TVD ($) - Tuvaluan Dollar</SelectItem>
                          <SelectItem value="TWD">TWD (NT$) - New Taiwan Dollar</SelectItem>
                          <SelectItem value="TZS">TZS (Sh) - Tanzanian Shilling</SelectItem>
                          <SelectItem value="UAH">UAH (₴) - Ukrainian Hryvnia</SelectItem>
                          <SelectItem value="UGX">UGX (Sh) - Ugandan Shilling</SelectItem>
                          <SelectItem value="UYU">UYU ($) - Uruguayan Peso</SelectItem>
                          <SelectItem value="UZS">UZS (so'm) - Uzbekistan Som</SelectItem>
                          <SelectItem value="VES">VES (Bs.) - Venezuelan Bolívar</SelectItem>
                          <SelectItem value="VND">VND (₫) - Vietnamese Dong</SelectItem>
                          <SelectItem value="VUV">VUV (Vt) - Vanuatu Vatu</SelectItem>
                          <SelectItem value="WST">WST (T) - Samoan Tala</SelectItem>
                          <SelectItem value="XAF">XAF (Fr) - Central African CFA Franc</SelectItem>
                          <SelectItem value="XCD">XCD ($) - East Caribbean Dollar</SelectItem>
                          <SelectItem value="XDR">XDR (SDR) - Special Drawing Rights</SelectItem>
                          <SelectItem value="XOF">XOF (Fr) - West African CFA Franc</SelectItem>
                          <SelectItem value="XPF">XPF (Fr) - CFP Franc</SelectItem>
                          <SelectItem value="YER">YER (﷼) - Yemeni Rial</SelectItem>
                          <SelectItem value="ZAR">ZAR (R) - South African Rand</SelectItem>
                          <SelectItem value="ZMW">ZMW (ZK) - Zambian Kwacha</SelectItem>
                          <SelectItem value="ZWL">ZWL ($) - Zimbabwean Dollar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Notes for Client</Label>
                      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for customer" rows={3} />
                    </div>

                    <div className="space-y-2">
                      <Label>Terms &amp; Conditions</Label>
                      <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Terms and conditions" rows={3} />
                    </div>

                    <div className="space-y-4 pt-2">
                      <div className="space-y-2">
                        <Label className="font-bold text-slate-800">1. Select Layout Format</Label>
                        <TemplatePicker selectedId={selectedTemplateId} onChange={setSelectedTemplateId} />
                      </div>

                      <div className="space-y-2">
                        <Label className="font-bold text-slate-800">2. Select Color Accent</Label>
                        <div className="flex flex-wrap gap-2.5 pt-1">
                          {[
                            { id: 'slate', name: 'Slate Gray', bg: 'bg-slate-500' },
                            { id: 'blue', name: 'Corporate Blue', bg: 'bg-blue-600' },
                            { id: 'black', name: 'Midnight Black', bg: 'bg-slate-950' },
                            { id: 'red', name: 'Crimson Red', bg: 'bg-red-600' },
                            { id: 'green', name: 'Emerald Green', bg: 'bg-emerald-600' },
                            { id: 'purple', name: 'Royal Purple', bg: 'bg-purple-600' },
                            { id: 'orange', name: 'Warm Amber', bg: 'bg-amber-500' },
                          ].map((theme) => {
                            const isSelected = colorTheme === theme.id;
                            return (
                              <button
                                key={theme.id}
                                type="button"
                                onClick={() => setColorTheme(theme.id)}
                                className={cn(
                                  "w-9 h-9 rounded-full flex items-center justify-center transition-all border-2",
                                  isSelected 
                                    ? "ring-2 ring-offset-2 ring-indigo-500 scale-105 border-white shadow-md" 
                                    : "border-transparent hover:scale-105 hover:shadow-sm"
                                )}
                                title={theme.name}
                              >
                                <span className={cn("w-7 h-7 rounded-full block shadow-inner", theme.bg)} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Right panel - Live Template Preview */}
          <div className="lg:col-span-7 flex flex-col gap-6 print:block print:w-full">
            {/* Header action panel */}
            <div className="bg-white border rounded-xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-4 print:hidden text-left">
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Live Preview</span>
                <div className="text-sm font-bold text-slate-800 mt-0.5">
                  {mode === 'invoice' ? `Invoice #${invoiceNumber}` : `Quotation #${quotationNumber}`}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" onClick={handleClearData} size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50/50 border-slate-200">
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Clear Data
                </Button>
                <Button variant="outline" onClick={handlePrint} size="sm">
                  <Printer className="h-4 w-4 mr-1.5" />
                  Print
                </Button>
                <Button onClick={handleDownloadPDF} disabled={downloading} size="sm">
                  <Download className="h-4 w-4 mr-1.5" />
                  {downloading ? 'Downloading...' : 'Download PDF'}
                </Button>
              </div>
            </div>

            {/* View container */}
            <div className="overflow-x-auto w-full print:w-full print:p-0 print:m-0 print:shadow-none">
              <div className="min-w-[600px] md:min-w-0 bg-white shadow-sm rounded-xl border border-slate-100 p-1 print:border-none print:shadow-none print:rounded-none print:p-0 print:m-0 print:bg-transparent">
                {mode === 'invoice' ? (
                  <InvoicePrintView invoice={invoiceData} elementId="try-invoice-content" noShadow />
                ) : (
                  <QuotationPrintView quotation={quotationData} elementId="try-quotation-content" noShadow />
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Advertisement Banner */}
        <div className="mt-12 flex flex-col items-center gap-2 print:hidden">
          <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Sponsored</span>
          <div className="max-w-full overflow-hidden rounded-lg shadow-sm border border-gray-200/85 bg-white p-2">
            <a href="https://easychurchmanagement.com/?ref=blafli" target="_blank" rel="noopener noreferrer" className="block">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/easychurchmanagement.firebasestorage.app/o/creative_assets%2Fimg_1782613220397_lkrolnz.png?alt=media&token=fa4b3530-7f2e-4337-bd2e-fe3ca3c93e63" 
                alt="Easy Church Management" 
                width="728" 
                height="90" 
                className="max-w-full h-auto mx-auto border-none block"
                style={{ border: 'none' }}
              />
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
